import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Download, Layers, Zap, Crown } from "lucide-react";
import { useAuth } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LIME = "#A4E832";
const CREAM = "#F5F2E6";
const PINK = "#FF2E9A";

export default function HistoryPage() {
  const { user, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // stash where the user wanted to go, then boot to sign-in
      sessionStorage.setItem("fb_return_to", "/history");
      signIn("/history");
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/history`, { withCredentials: true });
        setEntries(r.data.entries || []);
      } catch (e) {
        setEntries([]);
      } finally {
        setFetching(false);
      }
    })();
  }, [user, loading, signIn]);

  if (loading || !user) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-white/70 font-heading text-sm uppercase tracking-widest">
        loading…
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full pb-24"
      style={{
        background:
          "radial-gradient(1200px 800px at 20% 10%, rgba(255,46,154,0.08), transparent 60%), #070806",
        color: CREAM,
      }}
      data-testid="history-page"
    >
      {/* Top bar */}
      <div className="sticky top-0 z-20 backdrop-blur-lg bg-black/40 border-b border-white/10">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 md:px-6 py-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 font-heading text-xs md:text-sm uppercase tracking-widest text-white/85 hover:text-white"
            data-testid="history-back-btn"
          >
            <ArrowLeft size={16} strokeWidth={3} />
            Back
          </button>
          <div className="font-heading text-lg md:text-2xl tracking-tight">
            MY <span style={{ color: PINK }}>HISTORY</span>
          </div>
          <div className="w-16" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-6 md:pt-8">
        {/* Account header */}
        <div
          className="p-4 md:p-5 mb-6 border-2 rounded-sm"
          style={{ borderColor: LIME, background: "rgba(164,232,50,0.04)" }}
        >
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img
                src={user.picture}
                alt="account"
                className="w-12 h-12 rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center font-heading text-lg"
                style={{ background: PINK, color: "#000" }}
              >
                {(user.name || user.email).slice(0, 1).toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-heading text-base md:text-xl">
                {user.name}
              </div>
              <div className="font-body text-[10px] md:text-xs uppercase tracking-widest text-white/60 truncate">
                {user.email}
              </div>
            </div>
            {user.is_vip && (
              <div
                className="ml-auto flex items-center gap-1 font-heading text-[10px] md:text-xs uppercase tracking-widest px-2 py-1 rounded"
                style={{ background: LIME, color: "#000" }}
                data-testid="history-vip-badge"
              >
                <Crown size={12} strokeWidth={3} />
                Founding member #{user.vip_rank}
              </div>
            )}
          </div>
        </div>

        {/* Entries */}
        <div className="font-graffiti text-[10px] md:text-xs uppercase tracking-widest text-white/60 mb-3">
          Last 20 jobs
        </div>

        {fetching ? (
          <div className="font-body text-sm text-white/60">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="p-6 border border-dashed border-white/25 rounded-sm text-center">
            <div className="font-heading text-base uppercase mb-2">Nothing yet</div>
            <div className="font-body text-xs text-white/60 uppercase tracking-widest mb-4">
              Drop a track on FATBOY or DUMMY STEMS to see it here
            </div>
            <div className="flex gap-2 justify-center">
              <Link
                to="/"
                className="px-4 py-2 font-heading text-xs uppercase tracking-widest border-2 hover:translate-y-[-2px] transition-transform"
                style={{ background: PINK, color: "#000", borderColor: PINK }}
              >
                Go to FATBOY
              </Link>
              <Link
                to="/stems"
                className="px-4 py-2 font-heading text-xs uppercase tracking-widest border-2 hover:translate-y-[-2px] transition-transform"
                style={{ background: LIME, color: "#000", borderColor: LIME }}
              >
                Dummy Stems
              </Link>
            </div>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="history-list">
            {entries.map((e) => (
              <li
                key={`${e.kind}-${e.job_id}`}
                className="p-3 md:p-4 border-2 flex items-center gap-3 rounded-sm"
                style={{
                  borderColor: e.kind === "stems" ? LIME : PINK,
                  background: "rgba(7,8,6,0.55)",
                }}
                data-testid={`history-entry-${e.kind}-${e.job_id}`}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center border-2 rounded-sm shrink-0"
                  style={{
                    borderColor: e.kind === "stems" ? LIME : PINK,
                    color: e.kind === "stems" ? LIME : PINK,
                  }}
                >
                  {e.kind === "stems" ? <Layers size={18} strokeWidth={3} /> : <Zap size={18} strokeWidth={3} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-xs md:text-sm uppercase truncate">
                    {e.original_name || "track"}
                  </div>
                  <div className="font-body text-[10px] md:text-xs uppercase tracking-widest text-white/55">
                    {e.kind === "fatboy" ? `FATBOY · ${e.intensity}` : `${e.count} stems`}
                    {" · "}
                    {e.created_at?.slice(0, 10)}
                  </div>
                </div>

                {e.kind === "fatboy" ? (
                  <a
                    href={`${BACKEND_URL}${e.processed_url}`}
                    className="flex items-center gap-1 px-3 py-2 font-heading text-[10px] md:text-xs uppercase tracking-widest border-2 hover:translate-y-[-1px] transition-transform"
                    style={{ background: PINK, color: "#000", borderColor: PINK }}
                    data-testid={`history-dl-${e.job_id}`}
                  >
                    <Download size={12} strokeWidth={3} /> DL
                  </a>
                ) : (
                  <div className="flex flex-wrap justify-end gap-1">
                    {e.stems?.map((s, i) => (
                      <a
                        key={s.filename}
                        href={`${BACKEND_URL}${s.url}`}
                        download={s.filename}
                        className="px-2 py-1 font-heading text-[10px] uppercase border"
                        style={{ background: LIME, color: "#000", borderColor: LIME }}
                        data-testid={`history-stem-dl-${e.job_id}-${i + 1}`}
                      >
                        {i + 1}
                      </a>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
